import { useEffect, useMemo, useState } from "react";
import { Utensils, ChevronLeft, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import { fmtInt } from "@/lib/utils";
import { ItemCard } from "@/components/MealViewParts";

const TYPE_ORDER = ["breakfast", "lunch", "dinner", "snack"];
const TYPE_LABEL = {
  ar: { breakfast: "الفطور", lunch: "الغداء", dinner: "العشاء", snack: "سناك" },
  en: { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" },
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (iso, n) => { const [y, m, d] = iso.split("-").map(Number); return ymd(new Date(y, m - 1, d + n)); };
const toDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
const total = (meals, k) => meals.reduce((s, m) => s + Number(m[k] || 0), 0);

function WeekStrip({ byDate, selected, onPick, target, en }) {
  const today = ymd(new Date());
  const days = Array.from({ length: 7 }, (_, i) => shift(today, i - 6));
  return (
    <div className="grid grid-cols-7 gap-1 px-4 pb-3" dir="ltr">
      {days.map((d) => {
        const kcal = total(byDate[d] || [], "total_calories");
        const pct = target ? Math.min(kcal / target, 1.3) : 0;
        const over = target && kcal > target * 1.1;
        return (
          <button key={d} onClick={() => onPick(d)} data-testid={`shared-meals-week-${d}`}
            className={`rounded-xl py-1.5 flex flex-col items-center gap-1 transition-colors ${d === selected ? "bg-brand-500 text-white" : "bg-neutral-50 text-neutral-700 hover:bg-neutral-100"}`}>
            <span className="text-[9px] font-bold opacity-70">{toDate(d).toLocaleDateString(en ? "en" : "ar", { weekday: "short" })}</span>
            <span className="text-[11px] font-black num">{kcal ? fmtInt(kcal) : "—"}</span>
            <span className="w-6 h-1 rounded-full bg-black/10 overflow-hidden">
              <span className={`block h-full ${over ? "bg-red-400" : d === selected ? "bg-white" : "bg-emerald-400"}`} style={{ width: `${Math.min(pct, 1) * 100}%` }} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TotalsRow({ meals, user, en }) {
  const g = en ? "g" : "غ";
  const rows = [
    { k: "total_calories", t: user?.daily_calories, label: en ? "Calories" : "السعرات", unit: "", color: "text-neutral-900" },
    { k: "total_protein_g", t: user?.protein_g, label: en ? "Protein" : "بروتين", unit: g, color: "text-sky-600" },
    { k: "total_carbs_g", t: user?.carbs_g, label: en ? "Carbs" : "كارب", unit: g, color: "text-emerald-600" },
    { k: "total_fat_g", t: user?.fat_g, label: en ? "Fat" : "دهون", unit: g, color: "text-amber-700" },
  ];
  return (
    <div className="grid grid-cols-4 gap-1 px-4 pb-3" data-testid="shared-meals-totals">
      {rows.map((r) => (
        <div key={r.k} className="bg-neutral-50 rounded-xl py-2 text-center">
          <div className={`text-sm font-black num ${r.color}`}>{fmtInt(total(meals, r.k))}{r.unit && <span className="text-[9px] text-neutral-400">{r.unit}</span>}</div>
          {r.t ? <div className="text-[9px] text-neutral-400 num">/ {fmtInt(r.t)}{r.unit}</div> : null}
          <div className="text-[10px] text-neutral-500">{r.label}</div>
        </div>
      ))}
    </div>
  );
}

function MealRow({ m, en }) {
  const [open, setOpen] = useState(false);
  const g = en ? "g" : "غ";
  return (
    <div className="bg-white rounded-2xl border border-neutral-100" data-testid={`shared-meal-${m.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 p-3 text-start">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-neutral-900 truncate">{m.name || (en ? "Meal" : "وجبة")}</div>
          <div className="text-[10px] text-neutral-500 num mt-0.5">
            {fmtInt(m.total_protein_g)}{g} {en ? "P" : "ب"} · {fmtInt(m.total_carbs_g)}{g} {en ? "C" : "ك"} · {fmtInt(m.total_fat_g)}{g} {en ? "F" : "د"}
            {m.created_at && <> · {new Date(m.created_at).toLocaleTimeString(en ? "en" : "ar", { hour: "numeric", minute: "2-digit" })}</>}
          </div>
        </div>
        <div className="num font-black text-brand-500 text-sm">{fmtInt(m.total_calories)}<span className="text-[10px] text-neutral-400 ms-0.5">kcal</span></div>
        {(m.items || []).length > 0 && <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {(m.items || []).map((it, i) => <ItemCard key={i} it={it} en={en} />)}
          {m.notes && <div className="text-[11px] text-neutral-600 bg-neutral-50 rounded-lg p-2 whitespace-pre-wrap">{m.notes}</div>}
        </div>
      )}
    </div>
  );
}

export default function SharedMealsCard() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const en = lang === "en";
  const [date, setDate] = useState(ymd(new Date()));
  const [recent, setRecent] = useState([]);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const onErr = (e) => { if (e?.response?.status === 403) setHidden(true); };
  useEffect(() => { api.get("/meals").then((r) => setRecent(r.data || [])).catch(onErr); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/meals?date=${date}`).then((r) => setMeals(r.data || []))
      .catch((e) => { setMeals([]); onErr(e); }).finally(() => setLoading(false));
  }, [date]);

  const byDate = useMemo(() => recent.reduce((a, m) => { (a[m.date] ||= []).push(m); return a; }, {}), [recent]);
  const groups = TYPE_ORDER.map((t) => [t, meals.filter((m) => (TYPE_ORDER.includes(m.meal_type) ? m.meal_type : "snack") === t)]).filter(([, l]) => l.length);
  const isToday = date === ymd(new Date());

  if (hidden) return null;
  return (
    <div className="px-5 mb-4" dir={en ? "ltr" : "rtl"} data-testid="shared-meals-card">
      <div className="bg-white rounded-3xl shadow-card overflow-hidden border-[1.5px] border-black">
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="w-10 h-10 rounded-2xl bg-brand-500 text-white flex items-center justify-center"><Utensils className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="text-sm font-black text-neutral-900">{en ? "Daily meals" : "الوجبات اليومية"}</div>
            <div className="text-[11px] text-neutral-600">{en ? "Everything the trainee logged, day by day" : "كل ما سجّله المتدرب يوماً بيوم"}</div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => setDate((d) => shift(d, -1))} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100" data-testid="shared-meals-prev">
            {en ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="text-sm font-black" data-testid="shared-meals-date">
            {isToday ? (en ? "Today" : "اليوم") : toDate(date).toLocaleDateString(en ? "en" : "ar", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <button onClick={() => setDate((d) => shift(d, 1))} disabled={isToday} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100 disabled:opacity-30" data-testid="shared-meals-next">
            {en ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        <WeekStrip byDate={byDate} selected={date} onPick={setDate} target={user?.daily_calories} en={en} />
        <TotalsRow meals={meals} user={user} en={en} />
        <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3 space-y-3">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-neutral-400" /></div>
          ) : groups.length === 0 ? (
            <div className="text-[11px] text-neutral-400 text-center py-6" data-testid="shared-meals-empty">{en ? "No meals logged this day" : "لا توجد وجبات مسجلة في هذا اليوم"}</div>
          ) : groups.map(([t, list]) => (
            <div key={t} className="space-y-2">
              <div className="flex items-center justify-between text-[11px] font-black text-neutral-500">
                <span>{TYPE_LABEL[en ? "en" : "ar"][t]}</span>
                <span className="num">{fmtInt(total(list, "total_calories"))} kcal</span>
              </div>
              {list.map((m) => <MealRow key={m.id} m={m} en={en} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
